import React, { useEffect, useState } from 'react';

const DEFAULT_AVATAR_URI = '/resources/blank-user-avatar.png';

/**
 * Avatar for a deck's author, hotlinked from whichever site the deck was
 * imported from — so it fails in ways we don't control.
 *
 * `no-referrer` is load-bearing, not hygiene: static.mythichub.com sits behind
 * hotlink protection that answers 403 to any request carrying a cross-origin
 * Referer and 200 to one carrying none, so without it every MythicHub avatar
 * breaks. Anything still broken after that drops to the default rather than
 * leaving the browser's torn-image glyph on the page.
 *
 * Pass `renderFallback` to substitute your own markup instead of the default
 * image when there's nothing to show. `fallbackClassName` is added while the
 * default image shows, and `loadingClassName` until the image has landed.
 */
export function AuthorAvatar({
    avatarUrl,
    alt,
    renderFallback,
    className,
    fallbackClassName,
    loadingClassName,
    ...imgProps
}) {
    const [failed, setFailed] = useState(false);
    const [loaded, setLoaded] = useState(false);

    // List rows recycle as the user pages, so reset on a new URL — otherwise one
    // dead avatar pins the fallback onto every author that reuses the slot.
    useEffect(() => {
        setFailed(false);
        setLoaded(false);
    }, [avatarUrl]);

    const isBroken = failed || !avatarUrl;
    if (isBroken && renderFallback) {
        return renderFallback();
    }

    const classes = [className, isBroken && fallbackClassName, !loaded && loadingClassName].filter(Boolean).join(' ');

    return (
        <img
            {...imgProps}
            className={classes || undefined}
            src={isBroken ? DEFAULT_AVATAR_URI : avatarUrl}
            alt={alt || 'avatar'}
            referrerPolicy="no-referrer"
            onLoad={() => setLoaded(true)}
            onError={() => {
                setFailed(true);
                setLoaded(true);
            }}
        />
    );
}
